import { useAppState } from './app-state';
import { DeviceType, HostSdk } from '../types/host-sdk';
import { useCollapseExpand } from './collapse-expand';
import { useServices } from './services-registry';
import { ChatEventHooks } from '@wix/chat-web';
import { useCorvidApi } from './corvid-api';
import { Button } from '@wix/chat-sdk';

export const CTA_PRODUCT = {
  APP_DEFINITION_ID: '1380b703-ce81-ff05-f115-39571d94dfcd',
  SECTION_ID: 'product_page',
  SECTION_ID_IN_URL: 'product-page',
};

export const CTA_SERVICES = {
  APP_DEFINITION_ID: '13d21c63-b5ec-5912-8397-c3a5ddb27a97',
  SECTION_ID: 'Booking Service Page',
  SECTION_ID_IN_URL: 'service-page',
};

export const CTA_EVENTS = {
  APP_DEFINITION_ID: '140603ad-af8d-84a5-2c80-a0f60cb47351',
  SECTION_ID: 'events',
  SECTION_ID_IN_URL: 'event-details',
};

export const CTA_BLOGS = {
  APP_DEFINITION_ID: '14bcded7-0066-7c35-14d7-466cb3f09103',
  SECTION_ID: 'post',
  SECTION_ID_IN_URL: 'post',
};

export const CTA_PRICING_PLAN = {
  APP_DEFINITION_ID: '1522827f-c56c-a5c9-2ac9-00f9e6ae12d3',
  SECTION_ID: 'membership_plan_picker_tpa',
  SECTION_ID_IN_URL: 'plans-pricing',
};

interface CtaConfig {
  APP_DEFINITION_ID: string;
  SECTION_ID: string;
  SECTION_ID_IN_URL: string;
}

const handleNavigateToSection = ({
  name,
  ctaConfig,
  hostSdk,
  fallback,
}: {
  hostSdk: HostSdk;
  ctaConfig: CtaConfig;
  name: string;
  fallback: () => void;
}) => {
  hostSdk.navigateToSection(
    {
      appDefinitionId: ctaConfig.APP_DEFINITION_ID,
      sectionId: ctaConfig.SECTION_ID,
    },
    name,
    fallback,
  );
};

const handleNavigationToPricingPlans = ({
  hostSdk,
  url,
  fallback,
}: {
  hostSdk: HostSdk;
  url: string;
  fallback: () => void;
}) => {
  const _url = new URL(url);
  const queryParamsObject = _url.searchParams;
  const appSectionParams = queryParamsObject.get('appSectionParams');
  const parsedAppSectionParams = appSectionParams
    ? JSON.parse(appSectionParams)
    : '';

  hostSdk.navigateToSection(
    {
      appDefinitionId: CTA_PRICING_PLAN.APP_DEFINITION_ID,
      sectionId: CTA_PRICING_PLAN.SECTION_ID,
      queryParams: {
        ...(parsedAppSectionParams ? parsedAppSectionParams : {}),
      },
    },
    ``,
    fallback,
  );
};

export const useChatroomEventHooks = (): { eventHooks: ChatEventHooks } => {
  const collapseExpand = useCollapseExpand();
  const { appState, updateAppState } = useAppState();
  const { hostSdk, biLogger, experiments } = useServices();
  const corvidApi = useCorvidApi();
  const isMobile = appState.deviceType === DeviceType.Mobile;

  const isOpenV2ActionButtonsLinkInSameTabActive = experiments.enabled(
    'specs.chat.OpenV2ActionButtonsLinkInSameTab',
  );

  const onCallToActionClick = (url) => {
    biLogger.callToActionClickEvent(url);
    isMobile && collapseExpand.collapse();

    const requestAnimationFrameId = window.requestAnimationFrame(() => {
      const fallbackBehavior = () => hostSdk.openUrl(url);
      // This is the temporary solution for the chat assistant team to be able to navigate to page in same tab
      // This code provided for chat assistant team and on the first phase (September 2023) should be opened to the limited amount of partners
      // The editor team (talked to Gil Eckstein) should add to the hostSdk (https://dev.wix.com/docs/client/api-reference/deprecated/i-frame-sdk-deprecated/wix#navigatetopage)
      // method called "wixLocationFrontend" (For example: wixLocationFrontend.to("/product-page/i-m-a-product"))
      // additional info in slack thread: https://wix.slack.com/archives/C05RMQ4KCU8/p1693985980115589
      // P.S. Chat assistant team should care about adding this method to the hostSdk

      const productRegex = /product-page\/([^?/\s]+)/gm;
      const productMatch = productRegex.exec(url);
      if (productMatch) {
        const name = productMatch[1];
        handleNavigateToSection({
          name,
          ctaConfig: CTA_PRODUCT,
          hostSdk,
          fallback: fallbackBehavior,
        });

        return;
      }

      const serviceRegex = /service-page\/([^?/\s]+)/gm;
      const serviceMatch = serviceRegex.exec(url);
      if (serviceMatch && isOpenV2ActionButtonsLinkInSameTabActive) {
        const name = serviceMatch[1];
        handleNavigateToSection({
          name,
          ctaConfig: CTA_SERVICES,
          hostSdk,
          fallback: fallbackBehavior,
        });

        return;
      }

      const eventRegex = /event-details\/([^?/\s]+)/gm;
      const eventMatch = eventRegex.exec(url);
      if (eventMatch && isOpenV2ActionButtonsLinkInSameTabActive) {
        const name = eventMatch[1];
        handleNavigateToSection({
          name,
          ctaConfig: CTA_EVENTS,
          hostSdk,
          fallback: fallbackBehavior,
        });

        return;
      }

      const blogRegex = /post\/([^?/\s]+)/gm;
      const blogMatch = blogRegex.exec(url);
      if (blogMatch && isOpenV2ActionButtonsLinkInSameTabActive) {
        const name = blogMatch[1];
        handleNavigateToSection({
          name,
          ctaConfig: CTA_BLOGS,
          hostSdk,
          fallback: fallbackBehavior,
        });

        return;
      }

      const plansRegex = /plans(\?[^?\s]*)?/gm;
      const pricingPlansRegex = /plans-pricing\?([^?/\s]+)/gm;
      const pricingPlansMatch =
        pricingPlansRegex.exec(url) || plansRegex.exec(url);
      if (pricingPlansMatch && isOpenV2ActionButtonsLinkInSameTabActive) {
        handleNavigationToPricingPlans({
          hostSdk,
          url,
          fallback: fallbackBehavior,
        });

        return;
      }

      fallbackBehavior();
    });

    return () => {
      cancelAnimationFrame(requestAnimationFrameId);
    };
  };

  const onMessageReceived = () => {
    updateAppState({ hasMessages: true });
    biLogger.messageReceivedEvent(appState.shardId);
  };

  const onSendMessage = (message) => {
    corvidApi.reportMessageSent(message);
    const host = appState.host;
    const origin = 'Chat';
    biLogger.sendMessageEvent({ host, origin });
  };

  const onButtonInteraction = (button: Button) => {
    corvidApi.reportButtonInteraction(button);
    const host = appState.host;
    const buttonAppId = button.payload?.appId;
    const question_id = button.payload?.interactionId;
    biLogger.sendMessageEvent({ host, buttonAppId, question_id });

    if (button.cta) {
      hostSdk.openUrl(button.cta);
      biLogger.callToActionClickEvent(button.cta);
    }
  };

  const onAttachmentClick = () => biLogger.attachmentClickEvent();

  const eventHooks: ChatEventHooks = {
    onMessageReceived,
    onSendMessage,
    onAttachmentClick,
    onCallToActionClick,
    onButtonInteraction,
  };

  return {
    eventHooks,
  };
};
